// 120 — Intervenciones de Enfermería (SNS-MSP/HCU-form.120/2021, docs/msp/). Grilla fiel al papel (Fase 7, opcional): hasta
// 12 intervenciones NOMBRADAS (una por columna) × 24 horas del día de enfermería (8..7), con una celda de responsable por
// hora. Cada celda marcada crea un `Procedure` (como antes) con `performedDateTime`, `performer` y
// category = formularios-msp|120 (con eso `calidad-hc.ts` los cuenta como notas de enfermería en internación). Las
// intervenciones ya guardadas del día aparecen en su columna, marcadas y bloqueadas; guardar una celda ya guardada no la
// duplica. El responsable de cada celda es el practicante que guarda (nombre y apellido, como pide el instructivo).
// NO cambiar la categoría ni el `Procedure` (AccessPolicy de enfermería y `calidad-hc.ts` dependen de ella).
import type { Procedure } from '@medplum/fhirtypes';
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { FormularioDiscretoProps } from './index';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional, recursoRespuesta } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { reasonReferenceDeCondicion } from '../recetas/reasonReference';
import { construirProcedure120, diaEnfermeriaDe, fechaDeCelda, HORAS_DEL_DIA, rangoDiaEnfermeria, SISTEMA_FORMULARIOS_MSP } from './enfermeria';
import { celdasDeProcedures, celdasNuevas, hojasNecesarias120, horasGuardadas, NUM_PANELES_120, nombresGuardados, panelesVacios, paresIntervenciones120, responsableGuardado, type Panel120 } from './grilla120';
import { Responsable, SelectorDiaEnfermeria } from './SelectorTurno';
import { useResponsable } from './useResponsable';

/** Tope de hojas de continuación que ofrece la pantalla (12 intervenciones por hoja). */
const MAX_HOJAS_120 = 10;

export function Formulario120({ subject, encounter, conditionId, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const responsable = useResponsable();
  const pacienteRef = subject.reference ?? '';
  const [dia, setDia] = useState(() => diaEnfermeriaDe(new Date()));
  const [guardadas, setGuardadas] = useState<Procedure[]>([]);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [nuevos, setNuevos] = useState<Panel120[]>(() => panelesVacios(NUM_PANELES_120 * MAX_HOJAS_120));
  const [hojasPedidas, setHojasPedidas] = useState(1);
  const { guardando, error, guardar } = useGuardado();
  // Encabezado y pie oficiales del Anexo 1 (Fase 7, Tanda 2). "No. Hoja" lo numera el PDF ("Hoja n de N").
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Intervenciones de enfermería: el papel no trae servicio y suele ser hospitalario.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '120', t('formularios.f120.titulo'), { sinServicio: true });

  useEffect(() => {
    let cancelado = false;
    const { inicio, fin } = rangoDiaEnfermeria(dia);
    // `date` de Procedure = Procedure.performed; ge/lt repetidos -> query string a mano.
    medplum
      .searchResources(
        'Procedure',
        `patient=${encodeURIComponent(pacienteRef)}&category=${encodeURIComponent(`${SISTEMA_FORMULARIOS_MSP}|120`)}&date=ge${encodeURIComponent(inicio.toISOString())}&date=lt${encodeURIComponent(fin.toISOString())}&_count=500`,
      )
      .then((r) => !cancelado && setGuardadas(r as Procedure[]))
      .catch((err: unknown) => !cancelado && setErrorCarga(normalizeErrorString(err)));
    return () => {
      cancelado = true;
    };
  }, [medplum, pacienteRef, dia]);

  const bloqueadas = nombresGuardados(guardadas);
  // Columnas: primero las intervenciones ya guardadas del día (nombre fijo), luego las nuevas; 12 por hoja, con hojas de continuación
  // cuando el día ya tiene más de 12 intervenciones o se pide otra hoja (el papel se continúa en otra hoja, no se pierde nada).
  const hojas = Math.min(Math.max(hojasNecesarias120(bloqueadas.length), hojasPedidas), MAX_HOJAS_120);
  const columnasNuevas = Math.max(NUM_PANELES_120 * hojas - bloqueadas.length, 0);
  const nuevosVisibles = nuevos.slice(0, columnasNuevas);
  type Columna = { readonly tipo: 'g'; readonly nombre: string } | { readonly tipo: 'n'; readonly i: number; readonly panel: Panel120 };
  const columnas: Columna[] = [...bloqueadas.map((nombre): Columna => ({ tipo: 'g', nombre })), ...nuevosVisibles.map((panel, i): Columna => ({ tipo: 'n', i, panel }))];
  const hojasColumnas = Array.from({ length: hojas }, (_, h) => columnas.slice(h * NUM_PANELES_120, (h + 1) * NUM_PANELES_120));
  const pendientes = celdasNuevas(nuevosVisibles, guardadas);

  const cambiarPanel = (i: number, cambio: Partial<Panel120>): void => setNuevos((prev) => prev.map((p, j) => (j === i ? { ...p, ...cambio } : p)));
  const alternarHora = (i: number, h: number): void =>
    setNuevos((prev) => prev.map((p, j) => (j === i ? { ...p, horas: p.horas.includes(h) ? p.horas.filter((x) => x !== h) : [...p.horas, h] } : p)));

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      if (pendientes.length === 0) throw new Error(t('formularios.f120.sinIntervenciones'));
      // Una sola transacción: si Medplum rechaza una no se guarda ninguna, así reintentar no duplica intervenciones.
      const lote = new LoteTransaccional();
      const locales = pendientes.map((c) =>
        lote.crear<Procedure>(
          construirProcedure120({ intervencion: c.nombre, fecha: fechaDeCelda(dia, c.hora), subject, encounter, performer: responsable, reasonReference: reasonReferenceDeCondicion(conditionId) }),
        ),
      );
      const respuesta = await lote.enviar(medplum);
      setGuardadas((prev) => [...prev, ...locales.map((local, i) => recursoRespuesta(respuesta, i, local))]);
      // El PDF lleva TODO el día (lo ya guardado y lo de ahora) con el responsable de cada hora, no solo lo de este guardado.
      const quien = responsable?.display ?? t('enfermeria.sinResponsable');
      // El responsable actual solo se aplica a las celdas nuevas: las ya guardadas conservan el suyo (o "sin responsable").
      const celdasPdf = [
        ...celdasDeProcedures(guardadas),
        ...celdasDeProcedures(locales.map((l, i) => recursoRespuesta(respuesta, i, l))).map((c) => ({ ...c, responsable: c.responsable || quien })),
      ];
      const pares = [
        { pregunta: t('enfermeria.diaEnfermeria'), respuesta: dia },
        ...paresIntervenciones120(celdasPdf, t('formularios.f120.intervencion'), t('enfermeria.sinResponsable'), (n) => t('formularios.f120.hojaPdf', { n })),
      ];
      // Las columnas ya guardadas no se repiten; las nuevas quedan vacías para la siguiente tanda.
      setNuevos(panelesVacios(NUM_PANELES_120 * MAX_HOJAS_120));
      setHojasPedidas(1);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <div className="flex flex-wrap items-end gap-4">
        <SelectorDiaEnfermeria id="f120-dia" dia={dia} onChange={setDia} deshabilitado={pendientes.length > 0} />
        <Responsable display={responsable?.display} />
      </div>
      <p className="text-xs text-hce-muted">{t('formularios.f120.grillaNota')}</p>

      {errorCarga && (
        <Alert variant="red">
          <AlertDescription>{t('formularios.f120.errorCarga', { error: errorCarga })}</AlertDescription>
        </Alert>
      )}

      {hojasColumnas.map((cols, h) => (
        <div key={h} className="flex flex-col gap-1">
          {hojas > 1 && <p className="text-xs font-medium text-hce-ink">{t(h > 0 ? 'formularios.f120.hojaContinuacion' : 'formularios.f120.hoja', { n: h + 1, total: hojas })}</p>}
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="border-collapse text-sm">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th scope="col" className="sticky left-0 z-10 w-14 border-b border-border bg-hce-surface p-2 font-mono">{t('formularios.f120.hora')}</th>
                  {cols.map((c, k) => {
                    const numero = h * NUM_PANELES_120 + k + 1;
                    return c.tipo === 'g' ? (
                      <th key={`g-${c.nombre}`} scope="col" className="min-w-36 border-b border-l border-border p-1 align-top font-medium">
                        {c.nombre}
                      </th>
                    ) : (
                      <th key={`n-${c.i}`} scope="col" className="min-w-36 border-b border-l border-border p-1 align-top">
                        <CampoDictado
                          id={`f120-intervencion-${c.i}`}
                          etiqueta={`${t('formularios.f120.intervencion')} ${numero}`}
                          placeholder={`${t('formularios.f120.intervencion')} ${numero}`}
                          valor={c.panel.nombre}
                          onChange={(v) => cambiarPanel(c.i, { nombre: v })}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {HORAS_DEL_DIA.map((hora) => (
                  <tr key={hora}>
                    <th scope="row" className="sticky left-0 z-10 border-b border-border bg-hce-surface p-2 text-left font-mono font-normal">{hora}</th>
                    {cols.map((c, k) =>
                      c.tipo === 'g' ? (
                        <td key={`g-${c.nombre}`} className="border-b border-l border-border p-1 text-center">
                          {horasGuardadas(guardadas, c.nombre).has(hora) ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <Checkbox checked disabled aria-label={`${c.nombre} ${hora}:00`} />
                              <span className="text-[10px] leading-tight text-hce-muted">{responsableGuardado(guardadas, c.nombre, hora)}</span>
                            </div>
                          ) : null}
                        </td>
                      ) : (
                        <td key={`n-${c.i}`} className="border-b border-l border-border p-1 text-center">
                          <Checkbox
                            checked={c.panel.horas.includes(hora)}
                            disabled={!c.panel.nombre.trim()}
                            onCheckedChange={() => alternarHora(c.i, hora)}
                            aria-label={`${c.panel.nombre || `${t('formularios.f120.intervencion')} ${h * NUM_PANELES_120 + k + 1}`} ${hora}:00`}
                          />
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {hojas < MAX_HOJAS_120 && (
        <Button type="button" variant="outline" className="w-fit" onClick={() => setHojasPedidas(hojas + 1)}>
          {t('formularios.f120.agregarHoja')}
        </Button>
      )}

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando || pendientes.length === 0}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
